import { useQuery } from '@apollo/client';
import { GET_JOB_OFFER_DEACTIVATION_IMPACT } from '@gql/jobs';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';

interface JobOfferDeactivationDialogProps {
    open: boolean;
    jobOfferId: string;
    proId?: string;
    onClose: () => void;
    onConfirm: () => void;
    isLoadingSubmit: boolean;
}

const STATUS_LABELS: Record<string, string> = {
    PENDING: 'En attente',
    ACCEPTED: 'Acceptée',
    REJECTED: 'Rejetée',
    INTERVIEW: 'Entretien',
    SHORTLISTED: 'Présélectionnée',
    OFFER_SENT: 'Offre envoyée',
    // JobApplicationStatus keys
    New: 'Candidature',
    Interview: 'Entretien',
    Trial: 'Essai',
    Offer: 'Proposition',
    ProbationPeriod: "Période d'essai",
    Hired: 'Embauché',
    Rejected: 'Refusé',
};

const STATUS_COLORS: Record<string, string> = {
    PENDING: '#7C798B',
    ACCEPTED: '#8dd690',
    REJECTED: '#d34059',
    INTERVIEW: '#2f80ed',
    SHORTLISTED: '#f2994a',
    OFFER_SENT: '#119964',
    // JobApplicationStatus keys
    New: '#7C798B',
    Interview: '#2f80ed',
    Trial: '#f2994a',
    Offer: '#119964',
    ProbationPeriod: '#4239B0',
    Hired: '#8dd690',
    Rejected: '#d34059',
};

const JobOfferDeactivationDialog: React.FC<JobOfferDeactivationDialogProps> = ({
    open,
    jobOfferId,
    proId,
    onClose,
    onConfirm,
    isLoadingSubmit,
}) => {
    const theme = useTheme();

    const { data, loading, error } = useQuery(GET_JOB_OFFER_DEACTIVATION_IMPACT, {
        variables: { jobOfferId, proId },
        skip: !open || !jobOfferId,
        fetchPolicy: 'network-only',
    });

    const impact = data?.getJobOfferDeactivationImpact;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDialog-paper': {
                    width: '560px',
                    maxWidth: '560px',
                    borderRadius: '12px',
                    background: theme.palette.background.paper
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                }}
            >
                Désactiver l&apos;offre d&apos;emploi
            </DialogTitle>

            <DialogContent dividers>
                {loading && (
                    <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                        <CircularProgress size={32} />
                    </Box>
                )}

                {error && (
                    <Typography variant="body2" sx={{ color: theme.palette.errorRed.main }}>
                        Impossible de charger les données d&apos;impact.
                    </Typography>
                )}

                {impact && !loading && (
                    <>
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                            Êtes-vous sûr de vouloir désactiver{' '}
                            <strong style={{ color: theme.palette.text.primary }}>
                                {impact.jobOfferTitle}
                            </strong>{' '}
                            (#{impact.jobOfferNumber}) ?
                        </Typography>

                        {impact.total > 0 ? (
                            <>
                                <Box
                                    sx={{
                                        borderRadius: '8px',
                                        border: `1px solid ${theme.palette.errorRed.main}`,
                                        backgroundColor: `${theme.palette.errorRed.main}14`,
                                        px: 2,
                                        py: 1.5,
                                        mb: 2,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ color: theme.palette.errorRed.main, fontWeight: 600 }}
                                    >
                                        {impact.total} candidature{impact.total > 1 ? 's' : ''} sera
                                        {impact.total > 1 ? 'ont' : ''} impactée{impact.total > 1 ? 's' : ''}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                        Les candidatures en cours (hors embauches) seront rejetées.
                                    </Typography>
                                </Box>

                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 1, display: 'block' }}>
                                    Répartition par statut
                                </Typography>

                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {impact.byStatus.map((item: { status: string; count: number }) => {
                                        const statusColor = STATUS_COLORS[item.status] ?? theme.palette.text.secondary;
                                        return (
                                            <Chip
                                                key={item.status}
                                                label={`${STATUS_LABELS[item.status] ?? item.status} · ${item.count}`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: statusColor,
                                                    color: theme.palette.getContrastText(statusColor),
                                                    fontSize: '12px',
                                                    padding: '4px 8px',
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </>
                        ) : (
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Aucune candidature active ne sera impactée par cette désactivation.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button variant="text" color="secondary" onClick={onClose} disabled={isLoadingSubmit}>
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={onConfirm}
                    disabled={isLoadingSubmit || loading}
                    startIcon={isLoadingSubmit ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                    Désactiver
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default JobOfferDeactivationDialog;
